import { inject, Injectable } from '@angular/core';
import {
  outputToObservable,
  takeUntilDestroyed,
} from '@angular/core/rxjs-interop';
import { DialogService } from '@core/services/dialog.service';
import { RestApiService } from '@core/services/rest-api.service';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { FullScreenMapComponent } from '@features/map/full-screen-map/full-screen-map.component';
import {
  FullScreenMapProps,
  LayerEditEvent,
  MapInput,
  MapLayer,
} from '@features/map/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { TypedPOIService } from '@pages/poi-page/services/typed-poi.service';
import { RouteNetworkDataService } from '@pages/route-network-page/services/route-network-data.service';
import { Entity } from '@shared/constants/entity';
import { EventKey } from '@shared/constants/event-bus/event-key';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { RouteNetworkSegmentLine } from '@shared/interfaces/route-network';
import { EventBusService } from '@shared/services/event-bus/event-bus.service';
import {
  catchError,
  combineLatest,
  filter,
  map,
  merge,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import * as Leaflet from 'leaflet';
import { SupportUserService } from '@shared/services/support-user.service';
import { DvDialogEvent } from '@devinco/ui/dds';
import { ClientRouteNetworkStoreService } from './client-route-network-store.service';

@Injectable({ providedIn: 'root' })
export class RouteNetworkEditHelperService {
  private typedPoiService = inject(TypedPOIService);
  private snackbar = inject(DvSnackbarService);
  private translate = inject(TranslateService);
  private dataService = inject(RouteNetworkDataService);
  private api = inject(RestApiService);
  private dialog = inject(DialogService);
  private eventBusService = inject(EventBusService);
  private supportUserService = inject(SupportUserService);
  private clientRouteNetworkStore = inject(ClientRouteNetworkStoreService);

  private selectedPolylineIds: number[] = [];

  private segmentLineTooltipsOn = false;
  private segmentTooltipsOn = false;

  private mapComponent?: Leaflet.Map;
  private routeSegmentBtn?: HTMLAnchorElement;
  private routeSegmentLineBtn?: HTMLAnchorElement;

  private routeSegmentLineTooltips: Leaflet.Tooltip[] = [];

  onMapDialogReady(
    event: DvDialogEvent<FullScreenMapProps, FullScreenMapComponent>,
    routeNetworkEditExtendedEnabled: boolean,
    entityId: number,
    refetchRouteNetworkFn: () => void
  ): void {
    const fullScreenMap = event.context.contentComponent;
    if (!fullScreenMap) return;

    this.registerEditAndDeleteEvents(fullScreenMap, entityId);
    this.registerPolylineAddedEvents(
      fullScreenMap,
      entityId,
      refetchRouteNetworkFn
    );
    this.registerSplitSegmentEvents(
      fullScreenMap,
      routeNetworkEditExtendedEnabled,
      refetchRouteNetworkFn,
      entityId
    );
    this.registerPolylineClickEvents(
      routeNetworkEditExtendedEnabled,
      fullScreenMap,
      entityId
    );
    this.toggleSegmentAndLineTooltips(fullScreenMap);
  }

  registerEditAndDeleteEvents(
    fullScreenMap: FullScreenMapComponent,
    routeNetworkId: number
  ): void {
    fullScreenMap.fullScreenMapReady$
      .pipe(
        filter(ready => !!ready),
        switchMap(mapComponent =>
          merge(
            outputToObservable(mapComponent.polylinePointDeleted).pipe(
              map(e => ({ type: 'delete', event: e }))
            ),
            outputToObservable(mapComponent.polylinesEdited).pipe(
              map(e => ({ type: 'edit', event: e }))
            )
          )
        ),
        takeUntilDestroyed(fullScreenMap.destroyRef)
      )
      .subscribe(e => {
        this.clientRouteNetworkStore.registerRouteNetworkChange(routeNetworkId);
        if (e.type === 'delete') this.onPolylinePointDeleted(e.event);
        if (e.type === 'edit') this.onPolylinesEdited(e.event);
      });
  }

  registerPolylineAddedEvents(
    component: FullScreenMapComponent,
    entityId: number,
    callbackFn: () => void
  ): void {
    component.fullScreenMapReady$
      .pipe(
        filter(ready => !!ready),
        switchMap(map => outputToObservable(map.polylineAdded)),
        switchMap(event => this.saveNewPolyline(event, entityId)),
        tap(() =>
          this.clientRouteNetworkStore.registerRouteNetworkChange(entityId)
        ),
        takeUntilDestroyed(component.destroyRef)
      )
      .subscribe(() => callbackFn());
  }

  registerSplitSegmentEvents(
    component: FullScreenMapComponent,
    routeNetworkEditEnabled: boolean,
    callbackFn: () => void,
    entityId: number
  ): void {
    component.fullScreenMapReady$
      .pipe(
        filter(ready => !!ready),
        switchMap(map => outputToObservable(map.polyEditMarkerClicked)),
        switchMap(event =>
          this.openSplitConfirmDialog(event, routeNetworkEditEnabled)
        ),
        switchMap(event => this.splitSegmentIfAllowed(event)),
        tap(() =>
          this.clientRouteNetworkStore.registerRouteNetworkChange(entityId)
        ),
        takeUntilDestroyed(component.destroyRef)
      )
      .subscribe(resp => {
        if (resp) callbackFn();
      });
  }

  registerPolylineClickEvents(
    routeNetworkEditEnabled: boolean,
    component: FullScreenMapComponent,
    entityId: number
  ): void {
    if (!routeNetworkEditEnabled) return;

    component.fullScreenMapReady$
      .pipe(
        filter(Boolean),
        switchMap(map => outputToObservable(map.polylineClicked)),
        switchMap(event => {
          const polylineId = event.extraData?.polylineSegmentId;
          if (polylineId) {
            this.selectedPolylineIds.push(polylineId);
          }

          if (
            this.selectedPolylineIds.length === 2 &&
            // ensuring the same line was not selected twice
            new Set(this.selectedPolylineIds).size > 1
          ) {
            return this.dialog.open({
              contentText: 'RouteNetwork.MergeSegmentsDialogContent',
              title: 'General.Warning',
              confirmText: 'General.Confirm',
              cancelText: 'General.Cancel',
              type: 'warning',
              size: 'md',
            }).closed;
          } else {
            return of(undefined);
          }
        }),
        switchMap(resp => {
          if (!resp) return of(undefined);

          if (resp.source !== 'confirm') {
            this.selectedPolylineIds = [];
            return of(undefined);
          }

          return this.api
            .putCustom('Route/MergeSegments', this.selectedPolylineIds)
            .pipe(
              catchError(() => (this.selectedPolylineIds = [])),
              tap(() => {
                this.eventBusService.emit({
                  key: EventKey.RouteNetworkRefetchTriggered,
                  data: { doAutoFit: false },
                });
                this.selectedPolylineIds = [];
              })
            );
        }),
        tap(resp => {
          if (resp) {
            this.snackbar.open(
              this.translate.instant('RouteNetwork.SegmentsMergedSuccessfully'),
              SnackBarConfig.Success
            );
            this.clientRouteNetworkStore.registerRouteNetworkChange(entityId);
          }
        }),
        takeUntilDestroyed(component.destroyRef)
      )
      .subscribe();
  }

  constructMarkerInputValueFromCoords(
    lat: number | undefined,
    lng: number | undefined
  ): MapInput[] {
    return lat && lng
      ? [
          {
            points: this.typedPoiService.getTypedPOIMarkers(
              [
                {
                  GPSLat: lat,
                  GPSLon: lng,
                },
              ],
              true
            ),
          },
        ]
      : [];
  }

  toggleSegmentAndLineTooltips(component: FullScreenMapComponent): void {
    combineLatest([
      this.supportUserService.isSupportUser(),
      component.fullScreenMapReady$.pipe(
        filter(ready => !!ready),
        switchMap(map => outputToObservable(map.mapReady))
      ),
    ])
      .pipe(take(1))
      .subscribe(([isSupportUser, mapComponent]) => {
        if (!isSupportUser) return;
        this.mapComponent = mapComponent;
        this.addRouteSegmentTooltipControl(mapComponent);
        this.addRouteSegmentLineTooltipControl(mapComponent);
      });
  }

  setRouteSegmentTooltips(enabled: boolean): void {
    if (!this.mapComponent) {
      return;
    }

    this.mapComponent.eachLayer(layer => {
      if (
        layer instanceof Leaflet.Polyline &&
        !(layer instanceof Leaflet.Polygon)
      ) {
        const polyLayer: MapLayer = layer as MapLayer;
        if (!this.isPolylineEditable(polyLayer)) return;

        if (enabled) {
          polyLayer.bindTooltip(
            `<div>${this.translate.instant('General.RouteSegmentId')}: ${
              (layer as MapLayer).extraData?.polylineSegmentId
            }</div>`,
            { permanent: true }
          );
        } else {
          layer.unbindTooltip();
        }
      }
    });

    this.segmentTooltipsOn = enabled;
    this.routeSegmentBtn?.classList.toggle('debug-on', enabled);
  }

  public setRouteSegmentLineTooltips(enabled: boolean): void {
    if (!this.mapComponent) {
      return;
    }

    if (enabled) {
      this.mapComponent.eachLayer(layer => {
        if (
          layer instanceof Leaflet.Polyline &&
          !(layer instanceof Leaflet.Polygon)
        ) {
          if (!this.isPolylineEditable(layer)) return;

          this.addTooltipsToPolyline(layer as MapLayer, this.mapComponent!);
        }
      });
    } else {
      this.removeTooltips(this.mapComponent, this.routeSegmentLineTooltips);
    }

    this.segmentLineTooltipsOn = enabled;
    this.routeSegmentLineBtn?.classList.toggle('debug-on', enabled);
  }

  private isPolylineEditable(polyLayer: MapLayer): boolean {
    return !!polyLayer.extraData?.polylineData?.some(
      linePoint => !!linePoint.extraData?.editable
    );
  }

  private addRouteSegmentLineTooltipControl(mapComponent: Leaflet.Map): void {
    const RouteSegmentLineTooltipControl = Leaflet.Control.extend({
      options: {
        position: 'bottomleft',
        title: this.translate.instant('General.ShowRouteSegmentLineIds'),
        iconHtml: '<span class="dv-icons">draw_polyline</span>',
        onClick: () => {
          this.setRouteSegmentLineTooltips(!this.segmentLineTooltipsOn);
        },
      },

      onAdd: () => {
        const container = Leaflet.DomUtil.create(
          'div',
          'leaflet-bar leaflet-control line-debug-control'
        );

        const button = Leaflet.DomUtil.create(
          'a',
          '',
          container
        ) as HTMLAnchorElement;

        this.routeSegmentLineBtn = button;

        button.href = '#';
        button.title = RouteSegmentLineTooltipControl.prototype.options.title;
        button.setAttribute('role', 'button');
        button.innerHTML =
          RouteSegmentLineTooltipControl.prototype.options.iconHtml;

        Leaflet.DomEvent.disableClickPropagation(button);
        Leaflet.DomEvent.disableScrollPropagation(button);
        Leaflet.DomEvent.on(button, 'click', e => {
          Leaflet.DomEvent.stop(e);
          RouteSegmentLineTooltipControl.prototype.options.onClick();
        });

        return container;
      },
    });

    new RouteSegmentLineTooltipControl().addTo(mapComponent);
  }

  private addRouteSegmentTooltipControl(mapComponent: Leaflet.Map): void {
    const RouteSegmentDebugControl = Leaflet.Control.extend({
      options: {
        position: 'bottomleft',
        title: this.translate.instant('General.ShowRouteSegmentIds'),
        iconHtml: '<span class="dv-icons">axis_arrow_info</span>',
        onClick: () => {
          this.setRouteSegmentTooltips(!this.segmentTooltipsOn);
        },
      },

      onAdd: () => {
        const container = Leaflet.DomUtil.create(
          'div',
          'leaflet-bar leaflet-control segment-debug-control'
        );

        const button = Leaflet.DomUtil.create(
          'a',
          '',
          container
        ) as HTMLAnchorElement;

        this.routeSegmentBtn = button;

        button.href = '#';
        button.title = RouteSegmentDebugControl.prototype.options.title;
        button.setAttribute('role', 'button');
        button.innerHTML = RouteSegmentDebugControl.prototype.options.iconHtml;

        Leaflet.DomEvent.disableClickPropagation(button);
        Leaflet.DomEvent.disableScrollPropagation(button);
        Leaflet.DomEvent.on(button, 'click', e => {
          Leaflet.DomEvent.stop(e);
          RouteSegmentDebugControl.prototype.options.onClick();
        });

        return container;
      },
    });

    new RouteSegmentDebugControl().addTo(mapComponent);
  }

  private getAllLatLngs(polyline: L.Polyline): L.LatLng[] {
    const latlngs = polyline.getLatLngs();
    return latlngs.flat(Infinity) as L.LatLng[];
  }

  private addTooltipsToPolyline(polyline: MapLayer, map: Leaflet.Map): void {
    const points = this.getAllLatLngs(polyline as Leaflet.Polyline);
    const polylineData = polyline.extraData?.polylineData;

    points.forEach((latlng, index) => {
      const tooltip = Leaflet.tooltip({
        permanent: true,
        direction: 'top',
        className: 'vertex-tooltip',
      })
        .setLatLng(latlng)
        .setContent(
          `${this.translate.instant('General.RouteSegmentLineId')} ${
            polylineData?.[index]?.extraData?.id || '-'
          }`
        );

      this.routeSegmentLineTooltips.push(tooltip);
      tooltip.addTo(map);
    });
  }

  private removeTooltips(map: Leaflet.Map, tooltips: Leaflet.Tooltip[]): void {
    tooltips.forEach(tooltip => map.removeLayer(tooltip));
    this.routeSegmentLineTooltips = [];
  }

  private onPolylinesEdited(evt: LayerEditEvent): void {
    const payload: RouteNetworkSegmentLine[] = evt.markers.map(marker => ({
      Latitude: marker.lat,
      Longitude: marker.lng,
      RouteSegmentLineID: marker.extraData?.id,
      RouteSegmentID: marker.extraData?.polylineSegmentId,
    }));
    this.dataService.updateLines(payload).subscribe(() => {
      this.snackbar.open(
        this.translate.instant('RouteNetwork.RouteSegmentLineEditSuccess'),
        SnackBarConfig.Success
      );
    });
  }

  private onPolylinePointDeleted(evt: LayerEditEvent): void {
    const marker = evt.markers[0];
    if (marker.extraData) {
      this.dataService.deleteLines([marker.extraData.id!]).subscribe(() => {
        this.snackbar.open(
          this.translate.instant('RouteNetwork.RouteSegmentLineRemoveSuccess'),
          SnackBarConfig.Success
        );
      });
    }
  }

  private splitSegmentIfAllowed(
    event: LayerEditEvent | undefined
  ): Observable<unknown> {
    if (!event) return of(undefined);

    const segmentLineId = event.markers[0].extraData?.id;
    return this.api
      .putCustom(`Route/SplitSegment/${segmentLineId}`)
      .pipe(
        tap(() =>
          this.snackbar.open(
            this.translate.instant('RouteNetwork.SegmentSplitSuccessfully'),
            SnackBarConfig.Success
          )
        )
      );
  }

  private openSplitConfirmDialog(
    event: LayerEditEvent,
    routeNetworkEditEnabled: boolean
  ): Observable<LayerEditEvent | undefined> {
    if (!routeNetworkEditEnabled) return of(undefined);
    return this.dialog
      .open({
        contentText: 'RouteNetwork.SegmentSplitDialogContent',
        title: 'General.Warning',
        confirmText: 'General.Confirm',
        cancelText: 'General.Cancel',
        size: 'md',
        type: 'warning',
      })
      .closed.pipe(
        map(({ source }) => (source === 'confirm' ? event : undefined))
      );
  }

  private saveNewPolyline(
    event: LayerEditEvent,
    entityId: number
  ): Observable<number | undefined> {
    const payload = {
      RouteNetworkID: Number(entityId),
      RouteSegmentLines: event.markers.map((m, i) => ({
        Latitude: m.lat,
        Longitude: m.lng,
        SequenceNo: i + 1,
      })),
    };

    return this.api.upsert(Entity.RouteSegment, 0, payload);
  }
}
